import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { FileUp, Sparkles } from "lucide-react";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Message, MessageContent, Response } from "@/components/ai-elements/message";
import { Tool, ToolContent, ToolHeader } from "@/components/ai-elements/tool";

// A turn is one message. Assistant turns carry a tool summary + steps and the
// answer as a markdown string, which is streamed in token by token.
type Turn =
  | { role: "user"; text: string }
  | { role: "assistant"; toolTitle: string; steps: string[]; md: string };

type Story = { key: string; tab: string; icon: ReactNode; title: string; turns: Turn[] };

// All horse names, owners and file details are invented for the showcase —
// they don't reference real records.
const STORIES: Story[] = [
  {
    key: "import",
    tab: "Import a farrier list",
    icon: <FileUp className="size-4" aria-hidden />,
    title: "Import: Farrier visit log.xlsx",
    turns: [
      { role: "user", text: "Please import the 1 document(s) I just uploaded: Farrier visit log.xlsx." },
      {
        role: "assistant",
        toolTitle: "List batch files, Read document +23 more",
        steps: ["List batch files", "Read document", "Match horses by name", "Resolve ambiguous matches", "Stage 492 care actions"],
        md: `I've read **"Farrier visit log.xlsx"** and staged **137 horses** (56 matched and linked automatically) and **492 care actions**.

There are **16 horses** with ambiguous matches that need your clarification before we can commit the import. Please let me know whether each of these should be linked to an existing horse in the system, imported as a new horse, or skipped/removed:

### 1. High-probability matches (likely existing horses)

- **"Biscuit Mare"**: should this link to [Biscuit](/h)? Or is she a new horse?
- **"Ziggy"**: should this link to [Iggy (Ziggy)](/h)? Or is he a new horse?
- **"Maple"**: should this link to [Mabel](/h)? Or is she a new horse?
- **"Posey"**: should this link to [Posie](/h)? Or is she a new horse?
- **"Cleo"**: should this link to [Clio](/h)? Or is she a new horse?

### 2. Multiple match candidates

- **"Tessa"**: should this link to [Tess](/h) or [Tara](/h)? Or is she a new horse?
- **"Dolly"**: should this link to [Holly](/h) or [Dottie](/h)? Or is she a new horse?

### 3. No close matches found (likely new horses)

Should we create these as **new horses**, or does one of them match an existing horse with a different name?

- **"Pickle"**
- **"Domino"**
- **"Cricket"**
- **"Waffle"** (Note: we have [Wafer](/h) in the system, but no exact "Waffle" matches)
- **"Comet"**
- **"Ranger"**
- **"Juniper"**
- **"Scout"**
- **"Pretzel"**

Once you let me know how to resolve these, I'll update the staging and we can proceed with committing the batch!`,
      },
      { role: "user", text: "ok, Tessa is Tess, that one's right. everyone else is new" },
      {
        role: "assistant",
        toolTitle: "Revise staging, Revise staging +16 more",
        steps: ["Link Tessa to Tess", "Create 15 new horses", "Commit batch", "Write import to knowledge memory"],
        md: `I have successfully completed the import! Here is the summary of what was processed from **"Farrier visit log.xlsx"**:

- **Tessa** has been successfully linked to existing horse [Tess](/h).
- **15 other horses** (including *Biscuit Mare*, *Ziggy*, *Maple*, *Posey*, *Cleo*, *Dolly*, *Pickle*, *Domino*, *Cricket*, *Waffle*, *Comet*, *Ranger*, *Juniper*, *Scout*, and *Pretzel*) have been imported as brand-new horses, alongside **64 other new horses** detected in the file.
- **56 other horses** were automatically matched and linked to their existing profiles in the barn.
- **492 farrier care actions** have been recorded and added to the corresponding horses' histories.

All updates, records, and linked entities have been securely saved. You can view the details of this import session anytime in the knowledge memory: [Imported documents: Spring farrier visit](/h).

Let me know if there is anything else you need!`,
      },
    ],
  },
  {
    key: "ask",
    tab: "Ask about the herd",
    icon: <Sparkles className="size-4" aria-hidden />,
    title: "Who's overdue for the farrier?",
    turns: [
      { role: "user", text: "Which horses are overdue for a farrier visit?" },
      {
        role: "assistant",
        toolTitle: "Search care actions, List horses +3 more",
        steps: ["Search care actions", "List horses", "Compute cycle gaps", "Rank by overdue", "Format answer"],
        md: `Three horses are past the usual 6-8 week farrier cycle:

- [Domino](/h): last trim **11 weeks ago**
- [Cricket](/h): last trim **9 weeks ago**
- [Maple](/h): last trim **9 weeks ago**

Want me to draft a farrier visit and assign it?`,
      },
      { role: "user", text: "yes, book all three for next Tuesday" },
      {
        role: "assistant",
        toolTitle: "Create activity, Notify barn manager +2 more",
        steps: ["Create activity", "Attach 3 horses", "Notify barn manager", "Add to calendar"],
        md: `Done! I've scheduled a **farrier visit for next Tuesday** covering [Domino](/h), [Cricket](/h), and [Maple](/h), and notified the barn manager. It's on the calendar as [Farrier visit, Tue](/h).

Anything else you'd like me to line up?`,
      },
    ],
  },
];

const TICK_MS = 34;
const CHARS_PER_TICK = 7;

type PlayState = { done: number; active?: { i: number; phase: "tool" | "stream"; chars: number } };

/** If `n` falls inside a markdown link `[text](url)`, extend it to the link's
 *  end so links are revealed atomically while streaming. */
function snapToLinkBoundary(md: string, n: number): number {
  const re = /\[[^\]]*\]\([^)]*\)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(md))) {
    if (n > m.index && n < m.index + m[0].length) return m.index + m[0].length;
  }
  return n;
}

/** Fire once when the element first scrolls into view (same approach as the
 *  site's Reveal). Falls back to immediately-on if IntersectionObserver is
 *  unavailable. */
function useInViewOnce(ref: RefObject<Element | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined" || window.innerHeight === 0) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -15% 0px", threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return inView;
}

function Caret() {
  return (
    <span
      aria-hidden
      className="ml-0.5 inline-block h-[1.05em] w-[3px] translate-y-[0.15em] animate-pulse rounded-sm bg-carrot align-baseline"
    />
  );
}

function renderTurn(turn: Turn, key: number, opts: { phase?: "tool" | "stream"; chars?: number }) {
  if (turn.role === "user") {
    return (
      <Message key={key} from="user" className="coth-msg">
        <MessageContent>
          <p className="whitespace-pre-wrap">{turn.text}</p>
        </MessageContent>
      </Message>
    );
  }

  const running = opts.phase === "tool";
  const shown = opts.phase === undefined ? turn.md : turn.md.slice(0, opts.chars ?? 0);
  const streaming = opts.phase === "stream" && (opts.chars ?? 0) < turn.md.length;

  return (
    <Message key={key} from="assistant" className="coth-msg">
      <MessageContent>
        <Tool className="my-1">
          <ToolHeader
            state={running ? "input-streaming" : "output-available"}
            title={running ? "Working…" : turn.toolTitle}
          />
          <ToolContent>
            {turn.steps.map((s, i) => (
              <div key={i} className="text-sm text-muted-foreground">
                {s}
              </div>
            ))}
          </ToolContent>
        </Tool>
        {!running && (
          <div className="space-y-4">
            <Response>{shown}</Response>
            {streaming && <Caret />}
          </div>
        )}
      </MessageContent>
    </Message>
  );
}

function StoryView({ story, inView }: { story: Story; inView: boolean }) {
  const [state, setState] = useState<PlayState>({ done: 0 });
  const turns = story.turns;

  useEffect(() => {
    if (!inView) return;
    const reduce =
      typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setState({ done: turns.length });
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = (fn: () => void, ms: number) => {
      timer = setTimeout(() => !cancelled && fn(), ms);
    };

    const playTurn = (idx: number) => {
      if (cancelled || idx >= turns.length) return;
      const turn = turns[idx]!;
      if (turn.role === "user") {
        setState({ done: idx + 1 });
        schedule(() => playTurn(idx + 1), 750);
        return;
      }
      setState({ done: idx, active: { i: idx, phase: "tool", chars: 0 } });
      schedule(() => streamText(idx, 0), 1150);
    };

    const streamText = (idx: number, chars: number) => {
      if (cancelled) return;
      const turn = turns[idx]!;
      if (turn.role !== "assistant") return;
      if (chars >= turn.md.length) {
        setState({ done: idx + 1 });
        schedule(() => playTurn(idx + 1), 550);
        return;
      }
      // Reveal whole markdown links at once so a half-typed [text](url) never
      // flashes as raw syntax / "[blocked]".
      const next = snapToLinkBoundary(turn.md, Math.min(turn.md.length, chars + CHARS_PER_TICK));
      setState({ done: idx, active: { i: idx, phase: "stream", chars: next } });
      schedule(() => streamText(idx, next), TICK_MS);
    };

    playTurn(0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [inView, turns]);

  const items: ReactNode[] = [];
  for (let k = 0; k < state.done; k++) items.push(renderTurn(turns[k]!, k, {}));
  if (state.active) {
    const a = state.active;
    items.push(renderTurn(turns[a.i]!, a.i, { phase: a.phase, chars: a.chars }));
  }

  return (
    <section className="flex h-[32rem] flex-col overflow-hidden rounded-2xl border border-cream/20 bg-paper shadow-[0_30px_70px_-40px_rgba(0,0,0,0.6)] sm:h-[40rem] lg:h-[44rem]">
      <header className="flex items-center gap-3 border-b border-border/70 px-5 py-4">
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl bg-carrot-soft text-carrot-deep">
          {story.icon}
        </span>
        <div className="min-w-0">
          <p className="text-xs font-bold text-field-deep">Barn Assistant</p>
          <h3 className="truncate font-display text-base font-bold text-foreground sm:text-lg">{story.title}</h3>
        </div>
      </header>

      <Conversation className="min-h-0 flex-1">
        <ConversationContent className="mx-auto w-full max-w-3xl gap-8 p-4 sm:p-5">{items}</ConversationContent>
        <ConversationScrollButton className="border border-border bg-background text-ink hover:border-carrot hover:text-carrot-deep" />
      </Conversation>
    </section>
  );
}

/**
 * The interactive demo player. Renders as an embeddable block (no section
 * wrapper): the Root section provides the heading, dark background and the
 * #demo anchor. The chat panel itself stays a light "app window" on purpose,
 * because it is the real product surface.
 */
export function ChatShowcase() {
  const [active, setActive] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);
  const inView = useInViewOnce(stageRef);
  const story = STORIES[active]!;

  // Real tab semantics: arrow keys move and select, only the active tab is in
  // the tab order, and the panel is labelled by its tab.
  const onTablistKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const next =
      e.key === "ArrowRight" ? (active + 1) % STORIES.length : (active - 1 + STORIES.length) % STORIES.length;
    setActive(next);
    (e.currentTarget.querySelectorAll<HTMLButtonElement>("[role=tab]")[next])?.focus();
  };

  return (
    <div ref={stageRef}>
      <div
        role="tablist"
        aria-label="Example conversations"
        onKeyDown={onTablistKeyDown}
        className="mb-5 flex flex-wrap gap-2"
      >
        {STORIES.map((s, i) => {
          const selected = i === active;
          return (
            <button
              key={s.key}
              id={`demo-tab-${s.key}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls="demo-panel"
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(i)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                selected
                  ? "border-carrot-action bg-carrot-action font-bold text-white"
                  : "border-cream/25 bg-cream/[0.06] text-cream/85 hover:border-carrot/60 hover:text-cream"
              }`}
            >
              {s.icon}
              {s.tab}
            </button>
          );
        })}
      </div>

      {/* keyed so switching tabs remounts the player and replays from the top */}
      <div id="demo-panel" role="tabpanel" aria-labelledby={`demo-tab-${story.key}`}>
        <StoryView key={story.key} story={story} inView={inView} />
      </div>
    </div>
  );
}
